import { isArcLeaf, isPerspectiveLeaf, isPropertyLeaf } from '@center-platform/spheres/filters';
import type { FilterGroupLeaf } from 'types/filters';
import type { SimpleFilterGroup } from '../types/filterState';
import { nextFilterId, withStableIds } from './ids';

/**
 * Mints a stable id for one leaf, at the level that leaf keeps its identity on:
 * a property leaf carries `id` itself; a lane leaf (`{kind, filter}`) carries it
 * one level down on `filter.id`, because the wrapper is a discriminant, not a
 * persisted object — writing an `id` onto the wrapper would widen the canonical
 * `FilterGroupLeaf` shape on save. Mirrors `FilterGroupNodeMolecule`'s `leafKey`.
 */
const withStableLeafId = (leaf: FilterGroupLeaf): FilterGroupLeaf => {
  if (isPropertyLeaf(leaf)) return leaf.id ? leaf : { ...leaf, id: nextFilterId() };
  if (leaf.filter?.id) return leaf;
  // Rebuilt per discriminant rather than by spreading `leaf`: spreading a union
  // widens `filter` to the union of all three payloads, which no single member
  // accepts back.
  const id = nextFilterId();
  if (isPerspectiveLeaf(leaf)) return { kind: 'perspective', filter: { ...leaf.filter, id } };
  if (isArcLeaf(leaf)) return { kind: 'arc', filter: { ...leaf.filter, id } };
  return { kind: 'relationship', filter: { ...leaf.filter, id } };
};

/**
 * Normalises a group tree so every group and every leaf carries a stable id,
 * recursing into `groups` at every depth. Call once via `useMemo` keyed on
 * the top-level array identity — never per-node, so an unchanged tree never
 * re-mints ids for items that already have one.
 */
export const withStableFilterGroupIds = (groups: SimpleFilterGroup[]): SimpleFilterGroup[] =>
  withStableIds(groups).map((group) => ({
    ...group,
    filters: group.filters.map(withStableLeafId),
    groups: group.groups ? withStableFilterGroupIds(group.groups) : group.groups,
  }));
